import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Labeled, TextInput } from '@tes/ui';
import { PenLine, Trash2, Upload } from 'lucide-react';
import { HEX_RE } from '@tes/domain';
import { api } from '../lib/api';
import { messageOf } from '../lib/errors';
import { useSettings } from '../lib/useSettings';
import type { Branding, PlatformSettings } from '../lib/types';

const DEFAULT_PRIMARY = '#254eeb';

/** Paired native colour swatch + hex text field that stay in sync. */
function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  return (
    <Labeled label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={HEX_RE.test(value) ? value : DEFAULT_PRIMARY}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 flex-shrink-0 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
          aria-label={`${label} swatch`}
        />
        <TextInput
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#254eeb"
          spellCheck={false}
          className="font-mono"
        />
      </div>
    </Labeled>
  );
}

export function BrandingSettings() {
  const { data, isLoading } = useSettings();
  if (isLoading || !data) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-1 text-sm font-semibold text-slate-700">Signing page branding</h2>
        <p className="text-sm text-slate-400">Loading…</p>
      </section>
    );
  }
  const b = data.branding;
  // The form seeds its inputs ONCE from the server values. Remounting on the
  // saved text/colour values (not the logo URLs) means a save re-seeds from
  // what was written, while a logo upload leaves half-typed edits alone — and a
  // background refetch can never clobber what someone is typing.
  const version = `${b.companyName ?? ''}|${b.primaryColor ?? ''}|${b.accentColor ?? ''}`;
  return <BrandingForm key={version} branding={b} />;
}

function BrandingForm({ branding }: { branding: Branding }) {
  const qc = useQueryClient();
  const [companyName, setCompanyName] = useState(branding.companyName ?? '');
  const [primary, setPrimary] = useState(branding.primaryColor ?? DEFAULT_PRIMARY);
  const [accent, setAccent] = useState(branding.accentColor ?? DEFAULT_PRIMARY);
  const fileRef = useRef<HTMLInputElement>(null);
  const faviconRef = useRef<HTMLInputElement>(null);

  const onSaved = (updated: PlatformSettings) => qc.setQueryData(['settings'], updated);

  const saveColors = useMutation({
    mutationFn: () => api.patchBranding({ companyName, primaryColor: primary, accentColor: accent }),
    onSuccess: onSaved,
  });
  const uploadLogo = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.uploadBrandLogo(form);
    },
    onSuccess: onSaved,
  });
  const removeLogo = useMutation({ mutationFn: () => api.deleteBrandLogo(), onSuccess: onSaved });
  const uploadFavicon = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.uploadFavicon(form);
    },
    onSuccess: onSaved,
  });
  const removeFavicon = useMutation({ mutationFn: () => api.deleteFavicon(), onSuccess: onSaved });

  const hexInvalid = !HEX_RE.test(primary) || !HEX_RE.test(accent);
  const previewName = companyName.trim() || 'Your company';
  const saving =
    saveColors.isPending ||
    uploadLogo.isPending ||
    removeLogo.isPending ||
    uploadFavicon.isPending ||
    removeFavicon.isPending;
  const error =
    saveColors.error ??
    uploadLogo.error ??
    removeLogo.error ??
    uploadFavicon.error ??
    removeFavicon.error;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Signing page branding</h2>
        {saving && <span className="text-xs text-slate-400">Saving…</span>}
      </div>
      <p className="mb-4 text-xs text-slate-400">
        Shown to signers on the public signing page. The admin dashboard is unaffected.
      </p>
      {error && <p className="mb-3 text-sm text-red-600">{messageOf(error, 'Save failed')}</p>}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Controls */}
        <div className="space-y-4">
          <Labeled label="Company name">
            <TextInput
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Acme Pty Ltd"
              maxLength={80}
            />
          </Labeled>
          <ColorField label="Brand colour (buttons, header)" value={primary} onChange={setPrimary} />
          <ColorField label="Accent colour (signer fields)" value={accent} onChange={setAccent} />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => saveColors.mutate()} disabled={hexInvalid || saveColors.isPending}>
              Save branding
            </Button>
            {hexInvalid && <span className="text-xs text-red-600">Colours must be #rrggbb.</span>}
          </div>

          <div className="border-t border-slate-100 pt-4">
            <span className="mb-1 block text-xs font-medium text-slate-500">Logo</span>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-32 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-50">
                {branding.logoUrl ? (
                  <img src={branding.logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-xs text-slate-400">No logo</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo.mutate(f);
                    e.target.value = '';
                  }}
                />
                <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadLogo.isPending}>
                  <Upload size={14} className="mr-1" /> {branding.logoUrl ? 'Replace' : 'Upload'}
                </Button>
                {branding.logoUrl && (
                  <Button variant="ghost" size="sm" onClick={() => removeLogo.mutate()} disabled={removeLogo.isPending}>
                    <Trash2 size={14} className="mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-400">PNG, JPEG, or SVG · up to 2MB.</p>
          </div>

          <div className="border-t border-slate-100 pt-4">
            <span className="mb-1 block text-xs font-medium text-slate-500">Favicon</span>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-50">
                {branding.faviconUrl ? (
                  <img src={branding.faviconUrl} alt="Favicon" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-[9px] text-slate-400">None</span>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <input
                  ref={faviconRef}
                  type="file"
                  accept="image/png,image/svg+xml,image/x-icon,.ico"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFavicon.mutate(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => faviconRef.current?.click()}
                  disabled={uploadFavicon.isPending}
                >
                  <Upload size={14} className="mr-1" /> {branding.faviconUrl ? 'Replace' : 'Upload'}
                </Button>
                {branding.faviconUrl && (
                  <Button variant="ghost" size="sm" onClick={() => removeFavicon.mutate()} disabled={removeFavicon.isPending}>
                    <Trash2 size={14} className="mr-1" /> Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Browser-tab icon shown across the whole site. Square PNG/SVG/ICO · up to 1MB.
            </p>
          </div>
        </div>

        {/* Live preview — mirrors the signer's header, field, and button */}
        <div>
          <span className="mb-1 block text-xs font-medium text-slate-500">Preview</span>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={previewName} className="h-7 w-auto max-w-[140px] object-contain" />
              ) : (
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold text-white"
                  style={{ backgroundColor: HEX_RE.test(primary) ? primary : DEFAULT_PRIMARY }}
                >
                  {previewName.slice(0, 2).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-semibold tracking-tight text-slate-800">{previewName}</span>
            </div>
            <div className="space-y-3 bg-slate-50 p-4">
              <div
                className="flex h-12 items-center justify-center rounded border-2 border-dashed"
                style={{
                  borderColor: HEX_RE.test(accent) ? accent : DEFAULT_PRIMARY,
                  backgroundColor: `${HEX_RE.test(accent) ? accent : DEFAULT_PRIMARY}14`,
                  color: HEX_RE.test(accent) ? accent : DEFAULT_PRIMARY,
                }}
              >
                <span className="flex items-center gap-1 text-xs font-medium">
                  <PenLine size={12} /> signature
                </span>
              </div>
              <button
                type="button"
                className="w-full rounded-lg px-4 py-2 text-sm font-medium text-white"
                style={{ backgroundColor: HEX_RE.test(primary) ? primary : DEFAULT_PRIMARY }}
              >
                Finish signing
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
